<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MemberInfoHeader from '@/components/collectivites/MemberInfoHeader.vue';
import membersService from '@/services/membersService';
import { formatAmount, formatDateFrench } from '@/utils/formatters';
import { showAlert, showConfirm } from '@/utils/swal';

const route = useRoute();
const router = useRouter();

const memberId = computed(() => Number(route.params.id));
const member = ref<any>(null);
const items = ref<any[]>([]);
const stats = ref({ total_attendu: 0, total_effectue: 0, percent: 0 });
const loading = ref(false);
const validatingId = ref<number | null>(null);
const errorMessage = ref('');

function goBack() {
    router.push({ name: 'collectivites-membres' });
}

const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function moisLabel(detail: any): string {
    if (detail.date_prevision) {
        const d = new Date(detail.date_prevision);
        if (!isNaN(d.getTime())) return `${d.getDate().toString().padStart(2, '0')} ${monthsFr[d.getMonth()]} ${d.getFullYear()}`;
    }
    if (typeof detail.mois === 'number') return `Mois ${detail.mois}`;
    return '-';
}

async function loadAll() {
    if (!memberId.value) return;
    loading.value = true;
    errorMessage.value = '';
    try {
        const [m, p] = await Promise.all([
            membersService.getMember(memberId.value),
            membersService.getMemberPrelevementsMensuels(memberId.value),
        ]);
        member.value = m;
        items.value = p?.items || [];
        stats.value = p?.stats || { total_attendu: 0, total_effectue: 0, percent: 0 };
    } catch (e: any) {
        errorMessage.value = e?.message || 'Erreur lors du chargement.';
    } finally {
        loading.value = false;
    }
}

async function validateDetail(detail: any) {
    const ok = await showConfirm(
        `Valider ce prélèvement de ${formatAmount(detail.montant)} F ?`,
        'Confirmation'
    );
    if (!ok) return;

    validatingId.value = detail.id;
    try {
        await membersService.validateMemberPrelevementMensuel(memberId.value, detail.id);
        await showAlert.success('Prélèvement validé avec succès.');
        await loadAll();
    } catch (e: any) {
        showAlert.error(e?.message || 'Erreur lors de la validation.');
    } finally {
        validatingId.value = null;
    }
}

onMounted(loadAll);

const headerProgress = computed(() => ({
    label: 'Prélèvement',
    percent: stats.value.percent || 0,
    valueLabel: `${Math.round(stats.value.percent || 0)}%`,
}));

function statutTag(statut: string) {
    if (statut === 'done') return { class: 'notif', label: 'Effectué' };
    if (statut === 'cancelled') return { class: 'danger', label: 'Annulé' };
    return { class: 'warning', label: 'En attente' };
}
</script>

<template>
    <section class="container-full-1 bg-grey-50">
        <div class="d-flex align-items-center gap-4 mb-6">
            <button class="btn white2" @click="goBack">
                <img src="/public/icons/icon-back.svg" class="btn-icon" alt="" />
                <span>Retour</span>
            </button>
            <h2 class="secondary-font gap-4 mt-2 mb-0">Membre · Plan mensuel</h2>
        </div>

        <div v-if="loading" class="flex-center py-10"><div class="loader dark"></div></div>
        <div v-if="errorMessage" class="alert alert-danger mb-4">{{ errorMessage }}</div>

        <MemberInfoHeader
            :member="member"
            :end-date="member?.souscription?.date_fin"
            :progress="headerProgress"
        />

        <div class="clear-both h-20"></div>

        <!-- Prélèvement mensuel -->
        <div class="widget edit-card">
            <h3 class="edit-card-title">
                <img src="/public/icons/icon-data.svg" class="size-base" alt="" />
                Prélèvement mensuel
            </h3>

            <div class="progress-bar mb-6">
                <div class="progress-bar-fill bg-pop-green" :style="`width: ${Math.max(2, Math.min(100, stats.percent || 0))}%;`">
                    {{ formatAmount(stats.total_effectue) }} F / {{ formatAmount(stats.total_attendu) }} F
                </div>
                <span class="progress-bar-percent">{{ Math.round(stats.percent || 0) }}%</span>
            </div>

            <div v-if="items.length === 0" class="text-center text-grey-500 py-5">
                Aucun prélèvement prévu.
            </div>

            <div v-else class="prelevements-grid">
                <div v-for="detail in items" :key="detail.id" class="prelevement-card" :class="{ done: detail.statut === 'done' }">
                    <div class="prelevement-head">
                        <h6 class="font-semibold mb-1">{{ moisLabel(detail) }}</h6>
                        <h3 class="font-bold">{{ formatAmount(detail.montant) }} F</h3>
                    </div>
                    <div class="prelevement-meta">
                        <span class="tag-state stroke" :class="statutTag(detail.statut).class">
                            {{ statutTag(detail.statut).label }}
                        </span>
                        <p class="text-sm text-grey-500 mt-2 mb-3" v-if="detail.date_operation">
                            Le {{ formatDateFrench(detail.date_operation) }}
                        </p>
                        <p class="text-sm text-grey-500 mt-2 mb-3" v-else-if="detail.countdown">
                            {{ detail.countdown }}
                        </p>
                        <button
                            v-if="detail.statut === 'pending'"
                            class="btn-ultra w-100"
                            :disabled="validatingId === detail.id"
                            @click="validateDetail(detail)"
                        >
                            <div class="btn-show" v-if="validatingId !== detail.id">
                                <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
                                <span>Valider</span>
                            </div>
                            <div class="btn-loading" v-else>
                                <div class="loader-dots"></div>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="clear-both h-50"></div>
    </section>
</template>

<style scoped>
.edit-card {
    padding: 1.75rem;
}

.edit-card-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: rgb(var(--primary-color-rgb), 1);
    font-weight: 600;
    margin: 0 0 1.25rem;
}

.progress-bar {
    position: relative;
    height: 56px;
    border-radius: 100px;
    background-color: rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.progress-bar-fill {
    height: 100%;
    border-radius: 100px;
    display: flex;
    align-items: center;
    padding: 0 1.5rem;
    font-weight: 600;
    transition: width 0.3s ease;
    white-space: nowrap;
}

.progress-bar-percent {
    position: absolute;
    right: 1.25rem;
    top: 50%;
    transform: translateY(-50%);
    font-weight: 600;
}

.prelevements-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
}

.prelevement-card {
    border: 2px solid rgba(0, 0, 0, 0.08);
    border-radius: 20px;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    background: white;
    transition: all 0.2s ease;
}

.prelevement-card.done {
    border-color: rgba(var(--color-success-rgb), 0.4);
    background-color: rgba(var(--color-success-rgb), 0.04);
}

.prelevement-head h3 {
    margin: 0;
    color: rgba(0, 0, 0, 0.85);
}

.prelevement-meta {
    text-align: right;
}

.prelevement-meta p {
    margin: 0;
}

.w-100 {
    width: 100%;
    justify-content: center;
}
</style>
